import React, { useState } from "react";
import Skeleton from "react-loading-skeleton";
import Drawer from "components/ui-component/SideDrawer";
import { useNavigate, useParams } from "react-router-dom";
import Button from "components/form/Button";
import { ClockYellowIcon } from "icons/payments/ClockYellowIcon";
import { CloseYellowIcon } from "icons/payments/CloseYellowIcon";
import { DollarProcessIcon } from "icons/payments/DollarProcessIcon";
import { GreenTrueIcon } from "icons/payments/GreenTrueIcon";
import { InfoIcon } from "icons/payments/InfoIcon";
import { MoneyReciveIcon } from "icons/payments/MoneyReciveIcon";
import { RedCrossIcon } from "icons/payments/RedCrossIcon";
import { useDashboardData, useLeadData } from "store/slices/dashboardSlice";
import {
  downloadPdfFromApi,
  encode,
  formatCurrency,
  getPaymentMethod,
} from "utils/HelperFunctions";
import { NoPaymentsIcon } from "utils/icon";
import PaymentPreview from "components/payments/PaymentPreview";
import { useAppDispatch } from "store/hooks";
import { usePortalVanityUrl } from "store/slices/dashboardSlice";
import { paymentReceiptDownloadPdf } from "store/slices/paymentsSlice";
import CustomTooltip from "components/ui-component/CustomTooltip";
import { RefreshCircle } from "icons/payments/RefreshCircle";
import { decode } from "utils/HelperFunctions";
import { useMediaQuery } from "react-responsive";
import moment from "moment";
import { DangerIcon } from "icons/payments/DangerIcon";
import { TickSquare } from "icons/payments/TickSquare";
import ExcalimIcon from "icons/payments/ExclaimIcon";
import { TickCircle } from "icons/payments/TickCircle";
import { useTid } from "utils/testid";
import { formatDisplayDate } from "utils/DateFormat";

interface PaymentDetailsProps {
  isLoading: boolean;
  setFailedPaymentId: any;
  setFailedPayment: any;
}
const PaymentStatusIcons = ({
  payment,
  idSuffix = "",
  className = "",
}: {
  payment: any;
  idSuffix?: string;
  className?: string;
}) => {
  const tid = useTid();
  const showRecurring =
    payment?.customerid > 0 && payment?.payment_method !== "WTO";
  const showRecovered = payment?.recovered_payment === "Y";
  if (!showRecurring && !showRecovered) return null;
  return (
    <span className={className}>
      {showRecurring && (
        <CustomTooltip
          maxwidth="300px"
          id={`paymentTab-custom${payment?.id}${idSuffix}`}
          place="top"
          content={`<div class="font-normal text-xs leading-[18px]" > Recurring payment</div>`}
        >
          <div
            id={`paymentTab-custom${payment?.id}${idSuffix}`}
            className="w-fit cursor-pointer relative flex items-center"
            data-testid={tid(
              `recent-payment-${payment?.id}-recurring-indicator${idSuffix}`
            )}
          >
            <RefreshCircle aria-hidden="true" />
          </div>
        </CustomTooltip>
      )}
      {showRecovered && (
        <CustomTooltip
          maxwidth="264px"
          place="top"
          id={`payment-custom2${payment?.id}${idSuffix}`}
          content={`<div class="font-normal text-xs leading-[18px]" > Recovered payment${
            payment?.recovered_date
              ? `<br/>Original payment date: ${formatDisplayDate(
                  payment?.insert_date,
                  "MMM D, YYYY"
                )}`
              : ""
          }</div>`}
        >
          <div
            id={`payment-custom2${payment?.id}${idSuffix}`}
            className="w-fit cursor-pointer relative flex items-center"
            data-testid={tid(
              `recent-payment-${payment?.id}-recovered-indicator${idSuffix}`
            )}
          >
            <TickCircle aria-hidden="true" />
          </div>
        </CustomTooltip>
      )}
    </span>
  );
};

const RecentAndScheduledPayment = ({
  isLoading,
  setFailedPayment,
  setFailedPaymentId,
}: PaymentDetailsProps) => {
  const tid = useTid();
  const vanityUrl = usePortalVanityUrl();
  const dashboardData = useDashboardData();
  const navigate = useNavigate();
  const { clusterId: cluster_id } = useParams();
  const clusterId = decode(cluster_id || "", true);
  const paymentHistoryData =
    dashboardData?.recentPaymentData?.paymentHistoryData;
  const schedulePayment = dashboardData?.schedulePayment;
  const [isPreview, setIsPreview] = useState<boolean>(false);
  const [isPreviewId, setIsPreviewId] = useState<any>();
  const [isCustomSchPreviewId, setIsCustomSchPreviewId] = useState<any>();
  const [isScheduled, setIsScheduled] = useState<boolean>(false);
  const [isDownloadLoading, setIsDownloadLoading] = useState<string>("");
  const dispatch = useAppDispatch();
  const accountLeadData = useLeadData();
  const isLessThan590 = useMediaQuery({ query: "(max-width: 590px)" });

  const handleDownloadPDF = async (
    id: any,
    receiptId?: any,
    isWire: boolean = false
  ) => {
    setIsDownloadLoading(id);
    let pid = encode(`${id}_${clusterId}_${new Date().getTime()}`, "pid_");
    try {
      let payload = {
        invoice_id: id,
        lead_id: accountLeadData?.lead_id,
        url: `${vanityUrl}view-payment/${pid}`,
        cluster_id: clusterId,
        isFromPortal: true,
      };
      await downloadPdfFromApi(
        () => dispatch(paymentReceiptDownloadPdf(payload)).unwrap(),
        `${isWire ? "Wire transfer request" : "Receipt"} - ${
          receiptId || id
        }.pdf`,
        (loading) => setIsDownloadLoading(loading ? id : "")
      );
    } catch (error: any) {
      console.log("payment download err: -->", error?.message);
    }
  };

  return (
    <>
      <div
        className="w-full mt-10 mb-5 flex gap-[30px] pb-4 max-3xl:gap-4 half:flex-nowrap flex-wrap items-stretch"
        data-testid={tid("dashboard-payments-overview")}
      >
        <div
          className="half:w-[48%] w-full flex flex-col lg:gap-6 gap-4"
          data-testid={tid("recent-payments-section")}
          aria-label="Recent payments"
        >
          <div className="flex justify-between items-center">
            <p className="text-xl font-medium text-txtPrimary">
              Recent Payments
            </p>
            {Array.isArray(paymentHistoryData) &&
              paymentHistoryData.length > 0 && (
                <p
                  className="text-blue text-sm underline font-medium cursor-pointer hover:!text-btnPrimaryHover"
                  role="link"
                  tabIndex={0}
                  data-testid={tid("recent-payments-view-all-link")}
                  aria-label="View all recent payments"
                  onClick={() =>
                    navigate(`/${cluster_id}/payments`, {
                      state: { view: "payment" },
                    })
                  }
                >
                  View all
                </p>
              )}
          </div>
          {isLoading ? (
            <>
              {[0, 1, 2, 3, 4].map((key: number, index: number) => {
                return (
                  <div key={index}>
                    <div className="flex items-center justify-between py-[13px]">
                      <div className="w-[20%]">
                        <Skeleton />
                      </div>
                      <div className="w-[20%]">
                        <Skeleton />
                      </div>
                      <div className="w-[20%]">
                        <Skeleton />
                      </div>
                      <div className="w-[20%]">
                        <Skeleton />
                      </div>
                    </div>
                  </div>
                );
              })}
            </>
          ) : (
            // Pulled up by exactly the top padding now added to the first row,
            // so every row can share the same box for the hover highlight
            // without the list sitting any lower than before.
            <div className="w-full overflow-x-scroll -mt-[13px]">
              {Array.isArray(paymentHistoryData) &&
              paymentHistoryData.length > 0 ? (
                paymentHistoryData.map((recentPData: any, index1: number) => {
                  // The same id can repeat across rows, so pair it with the
                  // position to keep every key unique.
                  return (
                    <React.Fragment
                      key={`${recentPData?.id ?? "recent"}-${index1}`}
                    >
                      <div
                        data-testid={tid(
                          `recent-payment-row-${recentPData?.id}`
                        )}
                        role="button"
                        tabIndex={0}
                        aria-label={`Open recent payment ${recentPData?.id}`}
                        className={`flex flex-wrap lg:flex-nowrap items-center justify-between py-[13px] lg:h-[54px] hover:bg-bgThird cursor-pointer w-full gap-x-0 lg:gap-x-1 ${
                          isPreviewId === recentPData?.id ? "bg-bgThird" : ""
                        }`}
                        onClick={() => {
                          setIsPreviewId(recentPData?.id);
                          setIsScheduled(false);
                          setIsPreview(true);
                        }}
                      >
                        <div className="w-1/2 lg:w-fit lg:min-w-[70px] flex items-center gap-2 lg:gap-0">
                          <p className="text-lg lg:text-sm text-txtPrimary font-medium">
                            {formatCurrency(
                              recentPData.amount +
                                (recentPData?.payment_method === "ACH"
                                  ? (recentPData?.ach_fee_amount ?? 0)
                                  : 0) +
                                (recentPData?.payment_method !== "ACH"
                                  ? (recentPData?.cc_fee_amount ?? 0)
                                  : 0)
                            )}
                          </p>
                          <PaymentStatusIcons
                            payment={recentPData}
                            idSuffix="-m"
                            className="flex lg:hidden items-center gap-1"
                          />
                        </div>
                        <div className="w-1/2 lg:w-[155px] lg:min-w-[155px] flex gap-1 items-center justify-end lg:justify-start">
                          <div
                            className={`lg:max-w-[155px] text-sm max-w-[155px] w-fit order-last lg:order-none flex items-center py-[3px] px-[10px] gap-[6px] rounded-md ${
                              recentPData?.payment_status === "Succeeded"
                                ? "bg-[#23B3390D] text-green"
                                : recentPData?.payment_status === "Failed"
                                  ? "bg-[#FF00000D] text-red"
                                  : recentPData?.payment_status === "Refunded"
                                    ? "bg-[#1678F20D] text-blue"
                                    : recentPData?.payment_status ===
                                          "Voided" ||
                                        recentPData?.payment_status ===
                                          "Pending"
                                      ? "bg-[#FDB6000D] text-yellow"
                                      : recentPData?.payment_status ===
                                          "Partial refund"
                                        ? "bg-[#F3F8FE] text-blue"
                                        : recentPData?.payment_status ===
                                            "Disputed"
                                          ? "bg-[#4F576A0D] text-iconBright"
                                          : recentPData?.payment_status ===
                                              "Disputed lost"
                                            ? "bg-[#FF00000D] text-red"
                                            : recentPData?.payment_status ===
                                                "Disputed won"
                                              ? "bg-[#23B3390D] text-green"
                                              : recentPData?.payment_status ===
                                                  "Incomplete"
                                                ? "bg-[#4F576A0D] text-iconBright "
                                                : ""
                            }`}
                          >
                            {recentPData?.payment_status === "Succeeded" ||
                            recentPData?.payment_status === "Disputed won" ? (
                              <GreenTrueIcon />
                            ) : recentPData?.payment_status === "Failed" ||
                              recentPData?.payment_status ===
                                "Disputed lost" ? (
                              <RedCrossIcon />
                            ) : recentPData?.payment_status === "Refunded" ? (
                              <MoneyReciveIcon />
                            ) : recentPData?.payment_status === "Voided" ? (
                              <CloseYellowIcon />
                            ) : recentPData?.payment_status ===
                              "Partial refund" ? (
                              <DollarProcessIcon />
                            ) : recentPData?.payment_status === "Disputed" ? (
                              <InfoIcon />
                            ) : recentPData?.payment_status === "Pending" ? (
                              <ClockYellowIcon />
                            ) : recentPData?.payment_status ===
                              "Disputed lost" ? (
                              <DangerIcon />
                            ) : recentPData?.payment_status ===
                              "Disputed won" ? (
                              <TickSquare />
                            ) : recentPData?.payment_status === "Incomplete" ? (
                              <ExcalimIcon />
                            ) : (
                              ""
                            )}
                            {recentPData?.payment_status === "Disputed lost"
                              ? "Failed"
                              : recentPData?.payment_status === "Disputed won"
                                ? "Succeeded"
                                : recentPData?.payment_status}
                          </div>

                          {recentPData.payment_status === "Failed" ? (
                            <Button
                              text="Pay"
                              className="!text-xs !py-[3px] !px-2 cursor-pointer !leading-[15.62px]"
                              onClick={() => {
                                let encodeString = encode(
                                  `${
                                    recentPData?.id
                                  }_${clusterId}_${new Date().getTime()}`,
                                  "pid_"
                                );
                                setFailedPayment(true);
                                setFailedPaymentId(encodeString);
                              }}
                              data-testid={tid(
                                `recent-payment-${recentPData?.id}-pay-button`
                              )}
                              aria-label={`Pay failed payment ${recentPData?.id}`}
                            />
                          ) : (
                            ""
                          )}
                          <PaymentStatusIcons
                            payment={recentPData}
                            className="hidden lg:flex items-center gap-1"
                          />
                        </div>
                        <div className="w-1/2 lg:w-[100px] lg:min-w-[110px] order-2 lg:order-none mt-1.5 lg:mt-0 lg:text-base text-sm flex items-center justify-end lg:justify-start text-right gap-[2px]">
                          {getPaymentMethod({
                            card_type: recentPData.card_type,
                            method: recentPData.payment_method,
                            digits: recentPData.card_last_4_digits,
                            account_number:
                              recentPData?.account_number ||
                              recentPData?.card_last_4_digits,
                            className:
                              "!gap-[2px] justify-end lg:justify-start",
                            textClassName: "!text-txtSecondary",
                            isHidden: false,
                            ivr_type: recentPData?.ivr_type,
                            bank_name: recentPData?.bank_name,
                            rowId: recentPData?.id,
                          }) || "--"}
                        </div>
                        <div // whitespace-nowrap and a wider column: at 100px a date with a wide month
                          // name ("May 26, 2026") wrapped onto a second line while a narrow one
                          // ("Jun 23, 2026") fitted, so those rows grew to 74px and the two
                          // columns stopped lining up.
                          className="w-1/2 lg:w-[118px] lg:min-w-[118px] order-1 lg:order-none mt-1.5 lg:mt-0 lg:text-base text-sm whitespace-nowrap flex justify-start lg:justify-end text-txtSecondary"
                        >
                          {formatDisplayDate(
                            recentPData?.payment_date ?? recentPData?.insert_date,
                            "MMM D, YYYY"
                          )}
                        </div>
                      </div>
                    </React.Fragment>
                  );
                })
              ) : (
                <div className="w-full half:pt-[86px] pt-8 pb-8 flex justify-center">
                  <div className="flex flex-col items-center gap-4">
                    <NoPaymentsIcon aria-hidden="true" />
                    <p className="max-w-[420px] lg:text-base text-sm text-txtSecondary text-center">
                      No recent payments found. Your latest transactions will
                      appear here.
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="w-[1px] bg-strokePrimary self-stretch half:block hidden half:mt-[20px] half:mb-[30px]"></div>

        <div
          className="half:w-[48%] w-full flex flex-col lg:gap-6 gap-4 "
          data-testid={tid("scheduled-payments-section")}
          aria-label="Scheduled payments"
        >
          <div className="flex justify-between items-center">
            <p className="text-xl font-medium text-txtPrimary">
              Scheduled Payments
            </p>
            {Array.isArray(schedulePayment) && schedulePayment.length > 0 && (
              <p
                className="text-blue text-sm underline font-medium cursor-pointer hover:!text-btnPrimaryHover"
                role="link"
                tabIndex={0}
                data-testid={tid("scheduled-payments-view-all-link")}
                aria-label="View all scheduled payments"
                onClick={() =>
                  navigate(`/${cluster_id}/payments`, {
                    state: { view: "scheduled" },
                  })
                }
              >
                View all
              </p>
            )}
          </div>
          {isLoading ? (
            <>
              {[0, 1, 2, 3, 4].map((key: number, index: number) => {
                return (
                  <div key={index}>
                    <div className="flex items-center justify-between py-[13px]">
                      <div className="w-[20%]">
                        <Skeleton />
                      </div>
                      <div className="w-[20%]">
                        <Skeleton />
                      </div>
                      <div className="w-[20%]">
                        <Skeleton />
                      </div>
                      <div className="w-[20%]">
                        <Skeleton />
                      </div>
                    </div>
                  </div>
                );
              })}
            </>
          ) : (
            // Pulled up by exactly the top padding now added to the first row,
            // so every row can share the same box for the hover highlight
            // without the list sitting any lower than before.
            <div className="w-full overflow-x-scroll -mt-[13px]">
              {Array.isArray(schedulePayment) && schedulePayment.length > 0 ? (
                schedulePayment.map((schedulePData: any, index1: number) => {
                  return (
                    <React.Fragment
                      key={`${
                        schedulePData?.custom_schedule_id ?? "scheduled"
                      }-${index1}`}
                    >
                      <div
                        data-testid={tid(
                          `scheduled-payment-row-${schedulePData?.custom_schedule_id}`
                        )}
                        role="button"
                        tabIndex={0}
                        aria-label={`Open scheduled payment ${schedulePData?.custom_schedule_id}`}
                        // py-[13px] must match Recent Payments exactly, or the
                        // two columns drift a pixel further apart on every row.
                        className={`flex flex-wrap lg:flex-nowrap items-center justify-between py-[13px] lg:h-[54px] hover:bg-bgThird cursor-pointer w-full gap-x-0 lg:gap-x-1 ${
                          isCustomSchPreviewId ===
                            schedulePData?.custom_schedule_id && isScheduled
                            ? "bg-bgThird"
                            : ""
                        }`}
                        onClick={() => {
                          setIsPreviewId(schedulePData?.id);
                          setIsCustomSchPreviewId(
                            schedulePData?.custom_schedule_id
                          );
                          setIsScheduled(true);
                          setIsPreview(true);
                        }}
                      >
                        <p className="w-1/2 lg:w-fit lg:min-w-[70px] text-lg lg:text-sm text-txtPrimary font-medium">
                          {formatCurrency(
                            schedulePData.amount +
                              (schedulePData?.cf_amount ?? 0)
                          )}
                        </p>
                        <div className="w-1/2 lg:w-[155px] lg:min-w-[155px] flex justify-end lg:block">
                          <div
                            className={`lg:max-w-[155px] text-sm max-w-[155px] w-fit order-last lg:order-none flex items-center py-[3px] px-[10px] gap-[6px] rounded-md ${
                              schedulePData?.status === "Succeeded"
                                ? "bg-[#23B3390D] text-green text-sm"
                                : schedulePData?.status === "Failed"
                                  ? "bg-[#FF00000D] text-red text-sm"
                                  : schedulePData?.status === "Refunded"
                                    ? "bg-[#1678F20D] text-blue text-sm"
                                    : schedulePData?.status === "Voided" ||
                                        schedulePData?.status === "Pending"
                                      ? "bg-[#FDB6000D] text-yellow text-sm"
                                      : schedulePData?.status ===
                                          "partial refund"
                                        ? "bg-[#F3F8FE] text-blue text-sm"
                                        : schedulePData?.status === "disputed"
                                          ? "bg-[#4F576A0D] text-green text-sm"
                                          : ""
                            }`}
                          >
                            {schedulePData?.status === "Succeeded" ? (
                              <GreenTrueIcon />
                            ) : schedulePData?.status === "Failed" ? (
                              <RedCrossIcon />
                            ) : schedulePData?.status === "Refunded" ? (
                              <MoneyReciveIcon />
                            ) : schedulePData?.status === "Voided" ? (
                              <CloseYellowIcon />
                            ) : schedulePData?.status === "partial refund" ? (
                              <DollarProcessIcon />
                            ) : schedulePData?.status === "Disputed" ? (
                              <InfoIcon />
                            ) : schedulePData?.status === "Pending" ? (
                              <ClockYellowIcon />
                            ) : (
                              ""
                            )}
                            {schedulePData.status}
                          </div>
                        </div>
                        <div className="w-1/2 lg:w-[100px] lg:min-w-[110px] order-2 lg:order-none mt-1.5 lg:mt-0 lg:text-base text-sm flex items-center justify-end lg:justify-start text-right gap-[2px]">
                          {getPaymentMethod({
                            card_type: schedulePData.cc_type,
                            method: schedulePData.payment_method,
                            digits: schedulePData.cc_4digit,
                            account_number:
                              schedulePData?.account_number ||
                              schedulePData?.cc_4digit,
                            className:
                              "!gap-[2px] justify-end lg:justify-start",
                            textClassName: "!text-txtSecondary",
                            isHidden: false,
                            bank_name: schedulePData?.bank_name,
                            rowId: schedulePData?.custom_schedule_id,
                          }) || "--"}
                        </div>
                        <div // whitespace-nowrap and a wider column: at 100px a date with a wide month
                          // name ("May 26, 2026") wrapped onto a second line while a narrow one
                          // ("Jun 23, 2026") fitted, so those rows grew to 74px and the two
                          // columns stopped lining up.
                          className="w-1/2 lg:w-[118px] lg:min-w-[118px] order-1 lg:order-none mt-1.5 lg:mt-0 lg:text-base text-sm whitespace-nowrap flex justify-start lg:justify-end text-txtSecondary"
                        >
                          {schedulePData?.billing_date
                            ? moment(schedulePData?.billing_date).format(
                                "MMM D, YYYY"
                              )
                            : "-"}
                        </div>
                      </div>
                    </React.Fragment>
                  );
                })
              ) : (
                <div className="w-full half:pt-[86px] pt-8 pb-8 flex justify-center">
                  <div className="flex flex-col items-center gap-4">
                    <NoPaymentsIcon aria-hidden="true" />
                    <p className="max-w-[420px] lg:text-base text-sm text-txtSecondary text-center">
                      Your scheduled payments will appear here. You can set them
                      up to automate future transactions.
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      <Drawer
        enableOverlay={true}
        lockBackgroundScroll={true}
        direction="right"
        open={isPreview}
        size={isLessThan590 ? `100vw` : "590px"}
        onClose={() => {
          setIsPreview(false);
          setIsPreviewId("");
          setIsCustomSchPreviewId("");
        }}
        overlayColor="#000"
      >
        <PaymentPreview
          id={isPreviewId}
          setId={setIsPreviewId}
          show={isPreview}
          setShow={setIsPreview}
          onDownloadCLick={handleDownloadPDF}
          isDownloading={isDownloadLoading.toString()}
          isScheduled={isScheduled}
          custom_schedule_id={isCustomSchPreviewId}
        />
      </Drawer>
    </>
  );
};

export default RecentAndScheduledPayment;
